import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import TextInput from "../../components/inputs/TextInput";
import { Controller } from "react-hook-form";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import { useTranslation } from "react-i18next";
import { fileToBase64, mapToMultipleOptions, mapToOptions } from "../../lib/functions";
import ShowHidePasswordCard from "../../components/card/ShowHidePasswordCard";
import {
  allowedImagesTypes,
  API_ERRORS,
  passwordRequirements,
  passwordValidationInitialState,
  SelectInputOption,
  USER_ROLES_IDS,
} from "../../constants/common";
import { cn } from "../../utils/cn";
import PasswordSafetyCard from "../../components/card/PasswordSafetyCard";
import ComboboxInput from "../../components/inputs/ComboboxInput";
import {
  useGetADUserById,
  useGetAllADUsers,
  useGetAllBuildingsById,
  useGetAllDepartmentsById,
  useGetAllGatesById,
  useGetAllLocationsDropdown,
  useGetAllRoles,
} from "../../hooks/common/query";
import { Button } from "../../components/button";
import {
  useAddUser,
  useUpdateUser,
  useValidateUserEmail,
  useValidateUserName,
} from "../../hooks/user/mutation";
import useAuthStore from "../../store/Auth";
import toast, { Toaster } from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetUserById } from "../../hooks/user/query";
import { APP_ROUTES } from "../../routes";
import { createAddUsersSchema } from "../../schemas/addUsersSchema";
import MultipleComboboxInput from "../../components/inputs/MultipleComboboxInput";
import StatusCodes from "../../constants/StatusCodes";
import Loader from "../../components/loader";
import BackButton from "../../components/button/BackButton";
import SelectInput from "../../components/inputs/SelectInput";
import FileUploadInput from "../../components/inputs/FileUploadInput";
import ViewAttachmentModal from "../../components/modal/ViewAttachmentModal";

interface AddUserData {
  firstName?: string;
  lastName?: string;
  // dateOfBirth?: string;
  number?: string;
  email: string;
  location: string;
  building: string;
  visitingDepartment: string;
  gate?: string;
  username: string;
  password?: string;
  role: string;
  // uploadPhoto: any;
  uploadSignature?: any;
}

const initialState = {
  firstName: "",
  lastName: "",
  // dateOfBirth: "",
  number: "",
  email: "",
  location: "",
  visitingDepartment: "",
  username: "",
  password: "",
  role: "",
  activeDirectoryUser: "",
};

const AddUsers = () => {
  const params = useLocation();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const user = useAuthStore((state) => state.user);

  const key = useMemo(
    () => params.search.slice(1).split("ID=")[1],
    [params.search],
  );

  const uploadSignatureRef = useRef<HTMLInputElement | null>(null);
  const [signatureFileName, setSignatureFileName] = useState("");

  const [previewFiles, setPreviewFiles] = useState<
    Record<number, { url: string; contentType: string }>
  >({});
  const [imageSrc, setImageSrc] = useState("");
  const [isViewAttachmentModalOpen, setIsViewAttachmentModalOpen] =
    useState(false);
  const [contentType, setContentType] = useState("");

  const { data: userDetails, isLoading: isUserDetailsLoading } = useGetUserById(
    {
      encryptedId: key,
    },
  );

  const { mutateAsync: AddUser, isPending: isAddUserLoading } = useAddUser();
  const { mutateAsync: UpdateUser, isPending: isUpdateUserLoading } =
    useUpdateUser();

  const {
    mutateAsync: ValidateUserName,
    isPending: isValidateUserNameLoading,
  } = useValidateUserName();
  const {
    mutateAsync: ValidateUserEmail,
    isPending: isValidateUserEmailLoading,
  } = useValidateUserEmail();
  // const {
  //   mutateAsync: AddUserAttachment,
  //   isPending: isAddUserAttachmentLoading,
  // } = useAddUserAttachment();

  const isLoading = useMemo(
    () =>
      isAddUserLoading ||
      isUpdateUserLoading ||
      isValidateUserNameLoading ||
      isValidateUserEmailLoading,
    // || isAddUserAttachmentLoading
    [
      // isAddUserAttachmentLoading,
      isAddUserLoading,
      isUpdateUserLoading,
      isValidateUserEmailLoading,
      isValidateUserNameLoading,
    ],
  );

  const { data: allAdUsers, isLoading: isAllAdUsersLoading } =
    useGetAllADUsers();

  const [isAdUser, setIsAdUser] = useState(false);

  const [isUploadedSignature, setIsUploadedSignature] = useState(true);

  useEffect(() => {
    if (userDetails) {
      setIsAdUser(userDetails?.n_IsADUser);
      setIsUploadedSignature(userDetails?.haveSignatureFile);
    } else {
      setIsUploadedSignature(false);
    }
  }, [userDetails, key]);

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
    reset,
    clearErrors,
  } = useFormWithSchema(createAddUsersSchema(isAdUser, isUploadedSignature));

  const selectedEmployeeAccount = watch("activeDirectoryUser");
  const selectedRole = watch("role");
  const building = watch("building");

  // const uploadPhotoRef = useRef<HTMLInputElement | null>(null);
  // const [photoFileName, setPhotoFileName] = useState("");
  // const [photoFileType, setPhotoFileType] = useState("");

  const {
    data: adUser,
    error: adUserError,
    isLoading: isAdUserLoading,
  } = useGetADUserById({
    encryptedId: selectedEmployeeAccount || "",
  });

  // const [imageSrc, setImageSrc] = useState("");

  useEffect(() => {
    if (!key) {
      reset(initialState);
      setPasswordValidation(passwordValidationInitialState);
    }
  }, [key, reset]);

  const [isNewPasswordVisible, setIsNewPasswordVisible] =
    useState<boolean>(false);

  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    specialChar: false,
  });

  const validatePassword = useCallback((password: string) => {
    setPasswordValidation({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      specialChar: /[!@#$%^&*(),.?":{}|<>_+=-]/.test(password),
    });
  }, []);

  const handleTogglePasswordVisibility = useCallback(() => {
    setIsNewPasswordVisible((prev) => !prev);
  }, []);

  useEffect(() => {
    if (adUserError && (adUserError as any).status === StatusCodes.NOT_FOUND) {
      toast.error("User not found!");
    }
  }, [adUserError]);

  const validateADUserUsername = useCallback(async () => {
    try {
      const body = {
        encryptedId: key ? userDetails?.user_id?.toString() : null,
        s_Username: adUser?.account,
      };
      const { isExists } = await ValidateUserName(body);

      if (isExists) {
        toast.error(t("userAlreadyExists"));
        reset(initialState);
        setPasswordValidation(passwordValidationInitialState);
      } else {
        setValue("firstName", "");
        setValue("lastName", "");
        setValue("number", "");
        setValue("username", "");
        setValue("email", "");
        setValue("location", "");
        setValue("building", "");
        setValue("role", "");
        setValue("password", "");

        setValue("firstName", adUser?.firstName);
        setValue("lastName", adUser?.lastName);
        setValue("number", adUser?.mobile);
        setValue("username", adUser?.account);
        setValue("email", adUser?.email);

        setIsAdUser(true);
      }
    } catch (error) {
      toast.error(t("somethingWentWrong"));
      return;
    }
  }, [
    ValidateUserName,
    adUser?.account,
    adUser?.email,
    adUser?.firstName,
    adUser?.lastName,
    adUser?.mobile,
    key,
    reset,
    setValue,
    t,
    userDetails?.user_id,
  ]);

  const [
    selectedReceptionBuildingOptions,
    setSelectedReceptionBuildingOptions,
  ] = useState<SelectInputOption[]>([]);

  useEffect(() => {
    if (adUser) {
      validateADUserUsername();
    }
  }, [adUser, validateADUserUsername]);

  useEffect(() => {
    if (userDetails) {
      setValue("email", userDetails.email);
      setValue("firstName", userDetails.firstname);
      setValue("lastName", userDetails.lastname);
      userDetails.n_LocationID &&
        setValue("location", userDetails.n_LocationID);
      userDetails?.n_BuildingID &&
        setValue("building", userDetails?.n_BuildingID);
      userDetails?.n_GateID && setValue("gate", userDetails?.n_GateID);
      userDetails?.n_DepartmentID &&
        setValue("visitingDepartment", userDetails?.n_DepartmentID);
      userDetails.mobile && setValue("number", userDetails.mobile);
      userDetails.password && setValue("password", userDetails.password);
      userDetails.n_RoleID && setValue("role", userDetails.n_RoleID);
      setValue("username", userDetails.username);
      // userDetails.dt_DateOfBirth &&
      //   setValue("dateOfBirth", userDetails.dt_DateOfBirth);

      userDetails.password && validatePassword(userDetails.password);

      const receptionBuildings = userDetails?.userBuildings?.map(
        (item: {
          n_BuildingID: number;
          s_BuildingName_En: string;
          s_BuildingName_Ar: string;
        }) => ({
          value: item.n_BuildingID,
          label:
            i18n.language === "en"
              ? item.s_BuildingName_En
              : item.s_BuildingName_Ar,
          color: "#403f68",
        }),
      );

      setSelectedReceptionBuildingOptions(receptionBuildings || []);

      // if (userDetails?.s_Photo) {
      //   const url = `data:${userDetails?.s_PhotoContentType};base64,${userDetails?.s_Photo}`;
      //   setImageSrc(url);

      //   const file = base64ToFile(
      //     userDetails?.s_Photo,
      //     "user-image",
      //     userDetails?.s_PhotoContentType
      //   );
      //   setPhotoFileType(userDetails?.s_PhotoContentType);
      //   setValue("uploadPhoto", file);
      // }
    }
  }, [i18n.language, setValue, userDetails, validatePassword]);

  const { data: allLocations } = useGetAllLocationsDropdown();
  const { data: allRoles } = useGetAllRoles();

  const roles = useMemo(
    () => mapToOptions("s_RoleName", "s_RoleNameAr", "n_RoleID", allRoles),
    [allRoles],
  );

  const locations = useMemo(
    () =>
      mapToOptions(
        "s_LocationName_En",
        "s_LocationName_Ar",
        "n_LocationID",
        allLocations,
      ),
    [allLocations],
  );

  const selectedLocationId = watch("location");
  const selectedBuildingId = watch("building");

  const { data: allBuildings } = useGetAllBuildingsById({
    encryptedId: selectedLocationId
      ? selectedLocationId.toString()
      : userDetails?.location
        ? userDetails?.location.toString()
        : "",
  });

  const buildings = useMemo(
    () =>
      allBuildings
        ? mapToOptions(
          "s_BuildingName_En",
          "s_BuildingName_Ar",
          "n_BuildingID",
          allBuildings,
        )
        : [],
    [allBuildings],
  );

  const { data: allDepartments } = useGetAllDepartmentsById({
    encryptedDepartmentID: "0",
    encryptedLocationID: selectedLocationId
      ? selectedLocationId.toString()
      : userDetails?.location
        ? userDetails?.location.toString()
        : "",
    n_BuildingID: selectedBuildingId
      ? selectedBuildingId.toString()
      : userDetails?.building
        ? userDetails?.building.toString()
        : "",
  });

  const { data: allGates } = useGetAllGatesById({
    n_LocationID: selectedLocationId
      ? selectedLocationId.toString()
      : userDetails?.location
        ? userDetails?.location.toString()
        : "",
    n_BuildingID: selectedBuildingId
      ? selectedBuildingId.toString()
      : userDetails?.building
        ? userDetails?.building.toString()
        : "",
  });

  const departments = useMemo(
    () =>
      mapToOptions(
        "s_DepartmentName_En",
        "s_DepartmentName_Ar",
        "n_DepartmentID",
        allDepartments,
      ),
    [allDepartments],
  );

  const gates = useMemo(
    () => mapToOptions("s_GateName_En", "s_GateName_Ar", "n_GateID", allGates),
    [allGates],
  );

  const adUsers = useMemo(
    () =>
      mapToMultipleOptions(
        "name",
        "email",
        "employeeID",
        "account",
        allAdUsers,
      ),
    [allAdUsers],
  );

  const receptionBuildings = useMemo(() => {
    return (
      allBuildings
        // filter selected building
        ?.filter(
          (item: { n_BuildingID: number }) =>
            item.n_BuildingID !== Number(building),
        )
        .map(
          (item: {
            n_BuildingID: number;
            s_BuildingName_En: string;
            s_BuildingName_Ar: string;
          }) => ({
            value: item.n_BuildingID,
            label:
              i18n.language === "en"
                ? item.s_BuildingName_En
                : item.s_BuildingName_Ar,
            color: "#403f68",
          }),
        )
    );
  }, [allBuildings, building, i18n.language]);

  const handleAttachmentView = useCallback(
    async (type: number) => {
      if (previewFiles && previewFiles[type]) {
        setImageSrc(previewFiles[type].url);
        setContentType(previewFiles[type].contentType);
        setIsViewAttachmentModalOpen(true);
        return;
      }

      if (userDetails?.haveSignatureFile) {
        setImageSrc(`data:${userDetails?.s_SignatureContentType};base64,${userDetails?.s_SignatureFile}`);
        setContentType(userDetails?.s_SignatureContentType);
        setIsViewAttachmentModalOpen(true);
      }
    },
    [
      previewFiles,
      userDetails?.haveSignatureFile,
      userDetails?.s_SignatureContentType,
      userDetails?.s_SignatureFile,
    ],
  );

  const onSubmit = useCallback(
    async (data: AddUserData) => {
      try {
        const body = {
          encryptedId: key ? userDetails?.user_id?.toString() : null,
          s_Username: data.username,
        };

        const { isExists } = await ValidateUserName(body);
        if (isExists) {
          toast.error(t("usernameExists"));
          return;
        }
      } catch (error) {
        toast.error(t("somethingWentWrong"));
        return;
      }

      try {
        const body = {
          encryptedId: key ? userDetails?.user_id?.toString() : null,
          s_Email: data.email,
        };
        const { isExists } = await ValidateUserEmail(body);
        if (isExists) {
          toast.error(t("emailExists"));
          return;
        }
      } catch (error) {
        toast.error(t("somethingWentWrong"));
        return;
      }
      // let res;

      const receptionBuildings = selectedReceptionBuildingOptions.map(
        (item) => item.value,
      );

      try {
        const body = {
          N_RoleID: Number(data.role),
          firstname: data.firstName,
          lastname: data.lastName,
          fullname: `${data.firstName} ${data.lastName}`,
          username: data.username,
          ...(data.password && { password: data.password }),
          email: data.email,
          mobile: data.number,
          // Dt_DateOfBirth: data.dateOfBirth
          //   ? formattedDate(new Date(data.dateOfBirth), true)
          //   : null,
          N_LocationID: Number(data.location),
          n_BuildingID: Number(data.building),

          ...(Number(data.role) === USER_ROLES_IDS.RECEPTION && {
            userBuildings: receptionBuildings.map((id) => ({
              n_LocationID: Number(data.location),
              n_BuildingID: Number(id),
            })),
          }),

          N_IsActive: true,
          N_CreatedBy: user?.user_id,
          n_IsADUser: isAdUser ? 1 : 0,
          n_DepartmentID: Number(data.visitingDepartment),
          ...(Number(data.role) === USER_ROLES_IDS.RECEPTION && {
            n_GateID: data.gate ? Number(data.gate) : 0,
          }),

          haveSignatureFile: data.uploadSignature ? 1 : 0,
          ...(data.uploadSignature && {
            uploadSignatureBase64: await fileToBase64(data.uploadSignature),
            uploadSignatureContentType: data.uploadSignature.type
          }),
        };

        if (!key) {
          await AddUser(body);
        } else {
          const updateBody = {
            ...body,
            user_id: Number(key),
          };

          await UpdateUser(updateBody);
        }

        toast.success(t(!key ? "userAdded" : "userUpdated"));

        setTimeout(() => {
          navigate(APP_ROUTES.SEARCH_USERS);
        }, 700);
      } catch (error: any) {
        const errorCode = error.errors && Object.keys(error.errors)[0];

        if (errorCode === API_ERRORS.USER_BUILDINGS_REQUIRED) {
          toast.error(t("userMustContainAtleastOneBuilding"));
        } else {
          toast.error(t("somethingWentWrong"));
        }
        return;
      }

      // try {
      //   if (res) {
      //     const userId = res.id;

      //     const body = {
      //       Id: userId,
      //       File: data.uploadPhoto,
      //       S_FileType: photoFileType,
      //       S_FileFieldName: "S_Photo",
      //       S_FiledFileType: "S_PhotoContentType",
      //     };

      //     const formData = new FormData();
      //     formData.append("Id", body.Id.toString());
      //     formData.append("File", body.File ?? "");
      //     formData.append("S_FileType", body.S_FileType ?? "");
      //     formData.append("S_FileFieldName", body.S_FileFieldName);
      //     formData.append("S_FiledFileType", body.S_FiledFileType);

      //     await AddUserAttachment(formData);
      //     toast.success(t(!key ? "userAdded" : "userUpdated"));

      //     setTimeout(() => {
      //       navigate(APP_ROUTES.SEARCH_USERS);
      //     }, 700);
      //   }
      // } catch (error) {
      //   toast.error(t("somethingWentWrong"));
      //   return;
      // }
    },
    [
      selectedReceptionBuildingOptions,
      key,
      userDetails?.user_id,
      ValidateUserName,
      t,
      ValidateUserEmail,
      user?.user_id,
      isAdUser,
      AddUser,
      UpdateUser,
      navigate,
      fileToBase64,
      userDetails?.s_SignatureFile,
      userDetails?.s_SignatureContentType,
    ],
  );

  const handleClear = useCallback(() => {
    reset(initialState);
    setPasswordValidation(passwordValidationInitialState);
    setSelectedReceptionBuildingOptions([]);
  }, [reset]);

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-4 md:gap-8">
        <div className="flex items-center gap-1 sm:gap-2 justify-between">
          <h1 className="text-xl md:text-2xl dark:text-white">
            {!key ? t("addUser") : t("updateUser")}
          </h1>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={() => navigate(APP_ROUTES.SEARCH_USERS)}
              className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent dark:!ring-white dark:text-white"
            >
              {t("searchUsers")}
            </Button>
            <BackButton />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
          >
            {!key && isAllAdUsersLoading ? (
              <div className="flex flex-col gap-1">
                <label className="text-[13px] font-normal">
                  {t("activeDirectoryUsers")}
                </label>

                <Loader />
              </div>
            ) : !key ? (
              <div className="flex flex-col gap-1">
                <div className="grid sm:grid-cols-2">
                  <Controller
                    name="activeDirectoryUser"
                    control={control}
                    render={({ field: { onChange, name, value } }) => (
                      <MultipleComboboxInput
                        options={adUsers}
                        value={value}
                        onChange={onChange}
                        error={errors?.[name]?.message}
                        label={t("activeDirectoryUsers")}
                      />
                    )}
                  />
                </div>
                {allAdUsers && allAdUsers?.length > 0 && (
                  <p className="text-xs">
                    {t("aDUsers")}: {allAdUsers?.length}
                  </p>
                )}
              </div>
            ) : (
              <></>
            )}

            {key && (isAdUserLoading || isUserDetailsLoading) ? (
              <div className="flex items-center justify-center">
                <Loader />
              </div>
            ) : (
              <div className="flex flex-col gap-2 lg:gap-4">
                {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-2 lg:gap-4"> */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2 lg:gap-4">
                  {/* <div className="row-span-2">
                    <SingleAttachmentCard
                      control={control}
                      errors={errors}
                      uploadPhotoRef={uploadPhotoRef}
                      setPhotoFileName={setPhotoFileName}
                      setPhotoFileType={setPhotoFileType}
                      imageSrc={imageSrc}
                      setImageSrc={setImageSrc}
                    />
                  </div> */}

                  <Controller
                    name="firstName"
                    control={control}
                    render={({ field: { onChange, name, value } }) => (
                      <TextInput
                        label={t("firstName")}
                        onChange={onChange}
                        error={errors?.[name]?.message}
                        required={!isAdUser}
                        readOnly={isAdUser}
                        value={value || ""}
                      />
                    )}
                  />

                  <Controller
                    name="lastName"
                    control={control}
                    render={({ field: { onChange, name, value } }) => (
                      <TextInput
                        label={t("lastName")}
                        value={value || ""}
                        onChange={onChange}
                        required={!isAdUser}
                        readOnly={isAdUser}
                        error={errors?.[name]?.message}
                      />
                    )}
                  />

                  {/* <Controller
                    name="dateOfBirth"
                    control={control}
                    render={({ field: { onChange, name, value } }) => (
                      <DatePickerInput
                        label={t("dateOfBirth")}
                        value={value || ""}
                        required
                        onChange={onChange}
                        error={errors?.[name]?.message}
                        maxDate={new Date()}
                      />
                    )}
                  /> */}

                  <Controller
                    name="number"
                    control={control}
                    render={({ field: { onChange, name, value } }) => (
                      <TextInput
                        label={t("mobileNumber")}
                        onChange={onChange}
                        error={errors?.[name]?.message}
                        value={value || ""}
                        isInfoToolTip
                        infoToolTip={<p>{t("enterPhoneNumber")}</p>}
                      />
                    )}
                  />

                  <Controller
                    name="email"
                    control={control}
                    render={({ field: { onChange, name, value } }) => (
                      <TextInput
                        label={t("email")}
                        onChange={onChange}
                        error={errors?.[name]?.message}
                        required={!isAdUser}
                        readOnly={isAdUser}
                        value={value || ""}
                      />
                    )}
                  />

                  <Controller
                    name="username"
                    control={control}
                    render={({ field: { onChange, name, value } }) => (
                      <TextInput
                        label={t("username")}
                        onChange={onChange}
                        error={errors?.[name]?.message}
                        value={value || ""}
                        required={!isAdUser}
                        readOnly={isAdUser}
                      />
                    )}
                  />

                  <div className="relative flex flex-col gap-1">
                    <ShowHidePasswordCard
                      isPasswordVisible={isNewPasswordVisible}
                      handleTogglePasswordVisibility={
                        handleTogglePasswordVisibility
                      }
                      labelStyle="text-base"
                    />
                    <Controller
                      name="password"
                      control={control}
                      render={({ field: { onChange, value, name } }) => (
                        <TextInput
                          type={isNewPasswordVisible ? "text" : "password"}
                          label={t("password")}
                          value={value || ""}
                          onChange={(e) => {
                            onChange(e);
                            validatePassword(e.target.value);
                          }}
                          errorStyle="-bottom-[15px]"
                          error={errors?.[name]?.message}
                          required={!isAdUser}
                          readOnly={isAdUser}
                          isInfoToolTip
                          infoToolTip={
                            <ul
                              className={cn(
                                "list-disc text-xs flex flex-col",
                                i18n.language === "en" ? "ml-3" : "mr-3",
                              )}
                            >
                              {passwordRequirements.map(
                                (requirement, index) => (
                                  <li key={index}>{requirement}</li>
                                ),
                              )}
                            </ul>
                          }
                        />
                      )}
                    />

                    <PasswordSafetyCard
                      passwordValidation={passwordValidation}
                    />
                  </div>

                  <Controller
                    name="role"
                    control={control}
                    render={({ field: { onChange, name, value } }) => {
                      return (
                        <ComboboxInput
                          options={roles}
                          value={
                            roles?.find((item) => item.value === value)
                              ?.labelEn || ""
                          }
                          onChange={onChange}
                          required
                          error={errors?.[name]?.message}
                          label={t("role")}
                        />
                      );
                    }}
                  />

                  <Controller
                    name="location"
                    control={control}
                    render={({ field: { onChange, name, value } }) => (
                      <ComboboxInput
                        options={locations}
                        value={
                          locations?.find((item) => item.value === value)
                            ?.labelEn || ""
                        }
                        required
                        onChange={(value) => {
                          setValue("building", "");
                          setValue("gate", "");
                          setValue("visitingDepartment", "");
                          onChange(value);
                          // setSelectedLocationId(value);
                          clearErrors(name);
                        }}
                        error={errors?.[name]?.message}
                        label={t("location")}
                      />
                    )}
                  />

                  <Controller
                    name="building"
                    control={control}
                    render={({ field: { onChange, name, value } }) => (
                      <ComboboxInput
                        options={buildings}
                        value={
                          buildings?.find((item) => item.value === value)
                            ?.labelEn || ""
                        }
                        required
                        onChange={(value) => {
                          setValue("gate", "");
                          setValue("visitingDepartment", "");
                          onChange(value);
                          // setSelectedBuildingId(value);
                          clearErrors(name);

                          // remove building from selectedReceptionBuildingOptions if exists
                          setSelectedReceptionBuildingOptions((prev) =>
                            prev.filter((item) => item.value !== value),
                          );
                        }}
                        error={errors?.[name]?.message}
                        label={t("building")}
                      />
                    )}
                  />

                  <Controller
                    name="visitingDepartment"
                    control={control}
                    render={({ field: { onChange, name, value } }) => (
                      <ComboboxInput
                        options={departments}
                        value={
                          departments?.find((item) => item.value === value)
                            ?.labelEn || ""
                        }
                        onChange={(value) => {
                          onChange(value);
                          clearErrors(name);
                        }}
                        error={errors?.[name]?.message}
                        label={t("department")}
                        required
                      />
                    )}
                  />

                  {selectedRole?.toString() ===
                    USER_ROLES_IDS.RECEPTION.toString() && (
                      <Controller
                        name="gate"
                        control={control}
                        render={({ field: { onChange, name, value } }) => (
                          <ComboboxInput
                            options={gates}
                            value={
                              gates?.find((item) => item.value === value)
                                ?.labelEn || ""
                            }
                            onChange={(value) => {
                              onChange(value);
                              clearErrors(name);
                            }}
                            error={errors?.[name]?.message}
                            label={t("gate")}
                          />
                        )}
                      />
                    )}

                  <FileUploadInput
                    name="uploadSignature"
                    label={t("uploadSignature")}
                    accept=".png,.jpg,.jpeg"
                    allowedTypes={allowedImagesTypes}
                    control={control}
                    uploadRef={uploadSignatureRef}
                    fileName={signatureFileName}
                    setFileName={(val) => setSignatureFileName(val)}
                    error={errors.uploadSignature}
                    required
                    attachmentType={1}
                    handleAttachmentView={handleAttachmentView}
                    previewFiles={previewFiles}
                    setPreviewFiles={setPreviewFiles}
                    isAttachments={isUploadedSignature ? 1 : 0}
                    S_FileFieldName="signature"
                    s_FiledFileType="_"
                    onRemoveAttachment={() => setIsUploadedSignature(false)}
                  />
                </div>

                {building &&
                  selectedRole?.toString() ===
                  USER_ROLES_IDS.RECEPTION.toString() && (
                    <SelectInput
                      label={t("extraBuildings")}
                      options={receptionBuildings}
                      selectedOptions={selectedReceptionBuildingOptions}
                      setSelectedOptions={setSelectedReceptionBuildingOptions}
                    />
                  )}

                <div className="flex items-center gap-2 mt-3">
                  <Button isLoading={isLoading}>
                    {!key ? t("save") : t("update")}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleClear}
                    className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
                  >
                    {t("clear")}
                  </Button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>

      <ViewAttachmentModal
        attachment={imageSrc}
        contentType={contentType}
        isOpen={isViewAttachmentModalOpen}
        onOpenChange={() => {
          setIsViewAttachmentModalOpen(false);
          setImageSrc("");
        }}
      />
    </>
  );
};

export default AddUsers;
